/**
 * Form transformation and helper utilities
 */

/**
 * Sort sections by display_order
 */
export const sortSections = (sections) => {
  return [...(sections || [])]
    .map((section) => sortFieldsInSection(section))
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
};

/**
 * Sort fields within a section by display_order
 */
export const sortFieldsInSection = (section) => {
  if (!section.fields?.items) return section;

  return {
    ...section,
    fields: {
      ...section.fields,
      items: [...section.fields.items].sort(
        (a, b) => (a.display_order || 0) - (b.display_order || 0),
      ),
    },
  };
};

/**
 * Get all required sections
 */
export const getRequiredSections = (sections) => {
  return sections.filter((section) => section.is_required);
};

/**
 * Get grid columns based on span
 */
export const getGridColSpan = (gridSpan, totalCols = 1) => {
  return {
    xs: 24,
    sm: 12 * (gridSpan / totalCols),
    md: 12 * (gridSpan / totalCols),
    lg: 12 * (gridSpan / totalCols),
  };
};

/**
 * Generate unique field key for repeating sections
 */
export const generateRepeatingFieldKey = (baseFieldKey, instanceIndex) => {
  return `${baseFieldKey}_${instanceIndex}`;
};

/**
 * Parse repeating field key back to base and index
 */
export const parseRepeatingFieldKey = (fieldKey) => {
  const parts = fieldKey.split("_");
  const instanceIndex = parseInt(parts[parts.length - 1], 10);

  if (!isNaN(instanceIndex)) {
    const baseKey = parts.slice(0, -1).join("_");
    return { baseKey, instanceIndex };
  }

  return { baseKey: fieldKey, instanceIndex: 0 };
};

/**
 * Create empty instance for repeating section
 */
export const createEmptySectionInstance = (section) => {
  const instance = {};

  if (section.fields?.items) {
    section.fields.items.forEach((field) => {
      instance[field.field_key] = field.field_type === "boolean" ? false : null;
    });
  }

  return instance;
};

/**
 * Check if section is complete
 */
export const isSectionComplete = (section, formValues = {}) => {
  const requiredFields =
    section.fields?.items?.filter((f) => f.is_required) || [];

  return requiredFields.every(
    (field) =>
      formValues[field.field_key] !== null &&
      formValues[field.field_key] !== undefined &&
      formValues[field.field_key] !== "",
  );
};

/**
 * Get progress across all sections
 */
export const getFormProgress = (sections, formValues = {}) => {
  const totalFields = sections.reduce((count, section) => {
    return count + (section.fields?.items?.length || 0);
  }, 0);

  const filledFields = sections.reduce((count, section) => {
    const filled =
      section.fields?.items?.filter(
        (field) =>
          formValues[field.field_key] !== null &&
          formValues[field.field_key] !== undefined &&
          formValues[field.field_key] !== "",
      ).length || 0;

    return count + filled;
  }, 0);

  return Math.round((filledFields / totalFields) * 100) || 0;
};

/**
 * Build section label with icon
 */
export const buildSectionLabel = (section) => {
  const emoji = section.icon_emoji ? `${section.icon_emoji} ` : "";
  const badge = section.is_required ? " *" : "";
  return `${emoji}${section.label}${badge}`;
};

/**
 * Validate all required sections are filled
 */
export const validateRequiredSections = (sections, formValues = {}) => {
  const requiredSections = sections.filter((s) => s.is_required);

  return requiredSections.every((section) =>
    isSectionComplete(section, formValues),
  );
};

/**
 * Extract data from form by section
 */
export const extractFormDataBySection = (sections, formValues = {}) => {
  const data = {};

  sections.forEach((section) => {
    data[section.id] = {};

    if (section.fields?.items) {
      section.fields.items.forEach((field) => {
        if (formValues[field.field_key] !== undefined) {
          data[section.id][field.field_key] = formValues[field.field_key];
        }
      });
    }
  });

  return data;
};

/**
 * Merge section data back into form values
 */
export const mergeFormDataFromSection = (
  currentValues,
  sectionId,
  sectionData,
) => {
  return {
    ...currentValues,
    ...sectionData,
  };
};

/**
 * Get field by key from section
 */
export const getFieldFromSection = (section, fieldKey) => {
  return section.fields?.items?.find((f) => f.field_key === fieldKey);
};

/**
 * Get all fields from all sections
 */
export const getAllFields = (sections) => {
  const allFields = [];

  sections.forEach((section) => {
    if (section.fields?.items) {
      allFields.push(...section.fields.items);
    }
  });

  return allFields;
};

/**
 * Check if any section has repeating instances
 */
export const hasRepeatingInstances = (sections) => {
  return sections.some((s) => s.is_repeating);
};

/**
 * Get repeating instances data from form values
 */
export const getRepeatingInstancesData = (section, formValues = {}) => {
  if (!section.is_repeating) {
    return null;
  }

  const instances = {};
  const baseFieldKeys = section.fields?.items?.map((f) => f.field_key) || [];

  Object.keys(formValues).forEach((key) => {
    baseFieldKeys.forEach((baseKey) => {
      if (key.startsWith(`${baseKey}_`)) {
        const { baseKey: parsedBase, instanceIndex } =
          parseRepeatingFieldKey(key);

        if (!instances[instanceIndex]) {
          instances[instanceIndex] = {};
        }

        instances[instanceIndex][parsedBase] = formValues[key];
      }
    });
  });

  return Object.values(instances);
};
export const getDataWithLabels = ({ template, data }) => {
  const sections = {};

  // 🔹 Normalize sections + fields
  template?.sections.forEach((section) => {
    const fields = {};
    section.fields.forEach((f) => {
      fields[f.field_key] = f;
    });

    sections[section.id] = {
      ...section,
      fields,
    };
  });

  // 🔹 helper for value -> label
  const getLabel = (field, value) => {
    if (!field?.field_options) return value;

    const map = Object.fromEntries(
      field.field_options.map((opt) => [opt.value, opt.label]),
    );

    if (Array.isArray(value)) {
      return value.map((v) => map[v] ?? v);
    }

    return map[value] ?? value;
  };

  const final = {};

  Object.keys(data).forEach((sectionId) => {
    const section = sections[sectionId];
    if (!section) return;

    const sectionData = data[sectionId];

    // 🔁 Repeating section (array)
    if (Array.isArray(sectionData)) {
      final[section.label] = sectionData.map((item) => {
        const obj = {};

        Object.keys(item).forEach((fieldKey) => {
          const field = section.fields[fieldKey];
          if (!field) return;

          obj[field.label] = getLabel(field, item[fieldKey]);
        });

        return obj;
      });
    }

    // 📄 Single section (object)
    else {
      const obj = {};

      Object.keys(sectionData).forEach((fieldKey) => {
        const field = section.fields[fieldKey];
        if (!field) return;

        obj[field.label] = getLabel(field, sectionData[fieldKey]);
      });

      final[section.label] = obj;
    }
  });
  return final;
};
